import { hasClass } from '@opten/dom/has-class';
import { addClass } from '@opten/dom/add-class';
import { removeClass } from '@opten/dom/remove-class';
import { getParentWithClass } from '@opten/dom/get-parent-with-class';
import { scrollToElement } from '@opten/scroll/scroll-to-element';
import { toggleClass } from '@opten/dom/toggle-class';
import { parseCssUnit } from '@opten/number/parse-css-unit';
import { getStickyOffset } from '../../Opten.HypothekarbankLenzburg.Web.Static/app/utilities/scroll-helper';
import { getOffset } from '@opten/dom/get-offset';
import { getCss } from '@opten/dom/get-css';
import { isPad } from '../utilities/detection';

// todo: better way? global?
declare const picturefill;

export class FilterPageOptions {
    selectors?: {
        content: string;
        selects: string;
        selectTag: string;
        tag: string;
        tagCategory: string;
        dropdown: string;
        dropdownToggle: string;
        tagsDropdown: string;
        searchText: string;
        searchButton: string;
        tagAll: string;
        tagAllActive: string;
        tagActive: string;
        resetAllActiveTagsElements: string;
    };
    paging?: {
        pageBack: string;
        pageNext: string;
        pageJump: string;
        pageJumpAttribute: string;
        pagingSize: string;
    };
    loading?: {
        contentLoading: string;
        contentYScroll: number;
    };
    classes?: {
        active: string;
        hidden: string;
        hasActiveTags: string;
    };

    contentLocation?: string;
    wrapperId?: string;
    searchValueText?: string;
    tagValue?: string;
    scrollToTopAfterFiltering?: boolean;
    hasStickyOffsetDesktop?: boolean;
    callbackAfterFilter?: Function;
}

export class FilterPage {
    private isInitialPageLoad = true;
    private wrapperElement: HTMLElement;
    private wrapperId: number;
    private contentElement: HTMLElement;
    private contentLocation: string;
    private tags: Array<Element>;
    private filters: Array<string>;
    private activeTags: Array<string>;
    private selects: Array<Element>;
    private searchTextInput: HTMLInputElement;
    private searchTextButton: HTMLElement;
    private resetAllActiveTagsElements: Array<Element>;
    private allTags: Array<Element>;
    private pagingSize: number = 15;
    private page: number = 1;
    private language: string = 'de-CH';

    private _options: FilterPageOptions = {
        selectors: {
            content: 'js-filter__page__content',
            selects: 'js-filter__select',
            selectTag: 'js-filter__select__tag',
            tag: 'js-filter__tag',
            tagCategory: 'js-filter__tag--category',
            dropdown: 'js-filter__dropdown',
            dropdownToggle: 'js-filter__dropdown-toggle',
            tagsDropdown: 'js-filter-tags-dropdown',
            searchText: 'js-filter__search-text',
            searchButton: 'js-filter__search-button',
            tagAll: 'js-filter__tag-all',
            tagAllActive: 'js-filter__tag-all--active',
            tagActive: 'js-filter__tag-active',
            resetAllActiveTagsElements: 'js-filter__reset-all'
        },
        paging: {
            pageBack: 'js-filter__page-back',
            pageNext: 'js-filter__page-next',
            pageJump: 'js-filter__page-jump',
            pageJumpAttribute: 'data-value',
            pagingSize: 'data-paging-size'
        },
        loading: {
            contentLoading:
                '<div class="c-loading-indicator">Loading...</div>',
            contentYScroll: 0
        },
        classes: {
            active: '--active',
            hidden: '--hidden',
            hasActiveTags: 'has--active-tags'
        },
        contentLocation: 'data-location',
        wrapperId: 'data-id',
        searchValueText: 'search_text',
        tagValue: 'data-value',
        scrollToTopAfterFiltering: true,
        hasStickyOffsetDesktop: true
    };
    tagsCategory: Element[];

    constructor(private wrapper: HTMLElement, options?: FilterPageOptions) {
        this.wrapperElement = wrapper;
        this._options = { ...this._options, ...options };
        let dataLanguage = document.documentElement.lang;
		if (dataLanguage && dataLanguage.length > 0) {
			this.language = dataLanguage;
		}
        this.init();

        window.addEventListener('hashchange', () => this.filter());

        this.filter();

        this.initContentFilter();
    }

    private init() {
        this.wrapperId = Number(
            this.wrapperElement.getAttribute(this._options.wrapperId)
        );
        this.pagingSize = Number(
            this.wrapperElement.getAttribute(this._options.paging.pagingSize)
        );
        this.contentLocation = this.wrapperElement.getAttribute(
            this._options.contentLocation
        );
        this.contentElement = <HTMLElement>this.wrapperElement.getElementsByClassName(
            this._options.selectors.content
        )[0];
        this.selects = Array.from(
            this.wrapperElement.getElementsByClassName(
                this._options.selectors.selects
            )
        );
        this.tags = Array.from(
            this.wrapperElement.getElementsByClassName(
                this._options.selectors.tag
            )
        );
        this.tagsCategory = this.tags && this.tags.length > 0 ? this.tags.filter(o => hasClass(o, this._options.selectors.tagCategory)) : null;
        this.searchTextInput = <HTMLInputElement>this.wrapperElement.getElementsByClassName(
            this._options.selectors.searchText
        )[0];
        this.allTags = Array.from(
            this.wrapperElement.getElementsByClassName(
                this._options.selectors.tagAll
            )
        );
        this.resetAllActiveTagsElements = Array.from(
            this.wrapperElement.getElementsByClassName(
                this._options.selectors.resetAllActiveTagsElements
            )
        );

        this.tags.forEach(tag => {
            if (!hasClass(tag, this._options.selectors.selectTag)) {
                tag.addEventListener('click', () => this.toggleTag(tag));
            }
        });

        if (this.searchTextInput != null) {
            this.searchTextButton = <HTMLElement>this.searchTextInput.parentElement.getElementsByClassName(
                this._options.selectors.searchButton
            )[0];

            this.searchTextInput.addEventListener('keypress', e => {
                const key = e.which || e.keyCode;
                if (key === 13) {
                    // 13 is enter
                    this.loadFilterElements();
                }
            });

            if (this.searchTextButton != null) {
                this.searchTextButton.addEventListener('click', () =>
                    this.loadFilterElements()
                );
            }

            if (location.search.length > 0 && this.searchTextButton != null) {
                const searchValues = location.search.substring(1).split('=');
                for (const index in searchValues) {
                    if (searchValues[index] === this._options.searchValueText) {
                        let searchTextValue = searchValues[Number(index) + 1];
                        if (
                            searchTextValue !== null &&
                            searchTextValue !== ''
                        ) {
                            searchTextValue = decodeURIComponent(
                                searchTextValue
                            );
                        }
                        this.searchTextInput.value = searchTextValue;
                        break;
                    }
                }
                const query = window.location.search.substring(1);

                // is there anything there ?
                if (query.length) {
                    // are the new history methods available ?
                    if (
                        window.history !== undefined &&
                        window.history.pushState !== undefined
                    ) {
                        // if pushstate exists, add a new state the the history, this changes the url without reloading the page
                        window.history.pushState(
                            {},
                            document.title,
                            window.location.pathname
                        );
                    }
                }
            }
        }

        if (this.selects && this.selects.length > 0) {
            this.selects.forEach(select => {
                select.addEventListener('change', this.onChange);
            });
        }

        if (this.allTags && this.allTags.length > 0) {
            this.allTags.forEach(allTag =>
                allTag.addEventListener('click', () =>
                    this.allTagClick(
                        getParentWithClass(
                            <HTMLElement>allTag,
                            'js-filter-tags-dropdown'
                        )
                    )
                )
            );
        }

        if (
            this.resetAllActiveTagsElements &&
            this.resetAllActiveTagsElements.length > 0
        ) {
            this.resetAllActiveTagsElements.forEach(resetTags =>
                resetTags.addEventListener('click', () =>
                    this.resetAllActiveTags()
                )
            );
        }
    }

    private onChange(event: Event) {
        this.updateSelectTag(event.target as HTMLSelectElement);
    }

    public initContentFilter() {
        const that = this;
        if (this.contentElement != null) {
            const newSelects = Array.from(
                this.contentElement.getElementsByClassName(
                    this._options.selectors.selects
                )
            );
            const newTags = Array.from(
                this.contentElement.getElementsByClassName(
                    this._options.selectors.tag
                )
            );

            if (newTags && newTags.length > 0) {
                newTags.forEach(tag => {
                    if (!hasClass(tag, this._options.selectors.selectTag)) {
                        tag.addEventListener('click', () =>
                            this.toggleTag(tag)
                        );
                    }
                    that.tags.push(tag);
                });
            }
            if (newSelects && newSelects.length > 0) {
                newSelects.forEach(select => {
                    select.addEventListener('change', () =>
                        this.updateSelectTag(select)
                    );
                    that.selects.push(select);
                });
            }
        }
    }

    private loadFilterElements(setPageDefault: boolean = true) {
        if (setPageDefault) {
            this.setPageToDefault();
        }
        const searchText =
            this.searchTextInput != null ? this.searchTextInput.value : '';
        const dataSearch =
            'id=' +
            this.wrapperId +
            '&filter=' +
            this.getFilters() +
            '&pagingSize=' +
            this.pagingSize +
            '&page=' +
            this.page +
            '&searchText=' +
            searchText;

        this.contentElement.parentElement.style.overflow = 'hidden';
        this.contentElement.innerHTML = this._options.loading.contentLoading;

        fetch('/umbraco/surface/' + this.contentLocation, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                // todo: set language from chooser in the future
                'accept-language': this.language,
                // todo: why? if we change to a api controller we don't have this problem...
                'X-Requested-With': 'XMLHttpRequest' // otherwise accept-language is ignored
            },
            body: dataSearch
        })
            .then(response => {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    return response.text();
                }
            })
            .then(data => {
                if (data != null && data.length > 0) {
                    this.contentElement.parentElement.style.overflow = null;
                    this.contentElement.innerHTML = data;
                    this.setPreviousNextJumpPageEvents();
                    // this.initContentFilter();
                    this.reinitFilter();
                    picturefill();

                    // todo: change here to other offsettop ? this.contentElement.offsetTop + this._options.loading.contentYScroll;
                    // this.jumpToFilter();
                    if (this._options.scrollToTopAfterFiltering) {
                        window.scrollTo(0, 0);
                    }
                    else if (this.isInitialPageLoad == false) {
                        this._animate();
                    }
                    this.isInitialPageLoad = false;
                    if(this._options.callbackAfterFilter != undefined && this._options.callbackAfterFilter != null){
                        this._options.callbackAfterFilter();
                    }
                }
            });
    }

    private _animate() {
        const element = this.wrapperElement;

        if (element) {
            let offset = getOffset(element).top();

            offset += parseCssUnit(getCss(element, 'margin-top')) * -1;
            if (isPad() || (isPad() == false && this._options.hasStickyOffsetDesktop)) {
                offset += getStickyOffset() * -1;
            }

            offset = Math.round(offset);

            window.scrollTo(0, offset);
        }
    }

    private jumpToFilter() {
        if ((this.getFilters() === '') !== true) {
            const crossselling = document.getElementsByClassName('js-cross-selling-box')[0] as HTMLElement;
            if (crossselling) {
                const navbar = document.getElementsByClassName('c-header')[0] as HTMLElement;
                if (navbar) {
                    let cookieBannerHeight = 0;
                    if (navbar.style.top) {
                        cookieBannerHeight = parseInt(navbar.style.top, 10);
                    }
                    const offset = ((navbar.offsetHeight * -1) - cookieBannerHeight - 20);
                    scrollToElement(crossselling, undefined, 0, offset);
                }
            }
        }
    }

    private loadPreviousPage(event: any) {
        event.preventDefault();
        this.page = this.page - 1 >= 1 ? this.page - 1 : 1;
        this.loadFilterElements(false);
    }

    private loadNextPage(event: any) {
        event.preventDefault();
        this.page = this.page + 1;
        this.loadFilterElements(false);
    }

    private loadJumpPage(event: any) {
        event.preventDefault();
        const element = event.target || event.currentTarget;
        this.page = Number(
            element.getAttribute(this._options.paging.pageJumpAttribute)
        );
        this.loadFilterElements(false);
    }

    private setPreviousNextJumpPageEvents() {
        const pageBack = Array.from(
            this.contentElement.getElementsByClassName(
                this._options.paging.pageBack
            )
        );
        const pageNext = Array.from(
            this.contentElement.getElementsByClassName(
                this._options.paging.pageNext
            )
        );

        const pageJump = Array.from(
            this.contentElement.getElementsByClassName(
                this._options.paging.pageJump
            )
        );

        if (pageBack.length > 0) {
            pageBack.forEach(pageStep =>
                pageStep.addEventListener('click', e =>
                    this.loadPreviousPage(e)
                )
            );
        }
        if (pageNext.length > 0) {
            pageNext.forEach(pageStep =>
                pageStep.addEventListener('click', e => this.loadNextPage(e))
            );
        }

        if (pageJump.length > 0) {
            pageJump.forEach(pageStep =>
                pageStep.addEventListener('click', e => this.loadJumpPage(e))
            );
        }
    }

    private setPageToDefault() {
        this.page = 1;
    }

    private filter() {
        this.reinitFilter();
        this.loadFilterElements();
    }

    public reinitFilter() {
        this.activeTags = this.getTagsFromLocationHash();
        this.filters = [];
        this.setActiveTagsClasses();
        this.setFilterPageHasActiveTags();
    }

    private setActiveTagsClasses() {
        this.tags.forEach(tag => {
            const tagsDropdown = getParentWithClass(
                <HTMLElement>tag,
                this._options.selectors.tagsDropdown
            );
            if (tagsDropdown != null) {
                if (hasClass(tag, this._options.selectors.tagActive)) {
                    addClass(tag.parentElement, this._options.classes.hidden);
                } else {
                    removeClass(
                        tag.parentElement,
                        this._options.classes.hidden
                    );
                }
            }

            if (this.isTagActive(tag)) {
                if (tagsDropdown != null) {
                    this.deactivateAllTag(tagsDropdown);
                } else {
                    addClass(tag, this._options.classes.active);
                    if (this.isSelectOption(tag)) {
                        (<HTMLSelectElement>tag.parentElement).selectedIndex = (<HTMLOptionElement>tag).index;
                    }
                }
                this.addTagToFilters(tag);
            } else {
                if (tagsDropdown != null) {
                    const allTagsActive = Array.from(
                        tagsDropdown.getElementsByClassName(
                            this._options.selectors.tag +
                            ' ' +
                            this._options.selectors.tagActive
                        )
                    ).filter(
                        tag =>
                            hasClass(
                                tag.parentElement,
                                this._options.classes.hidden
                            ) !== true
                    );
                    if (allTagsActive.length === 0) {
                        this.activateAllTag(tagsDropdown);
                    }
                } else {
                    removeClass(tag, this._options.classes.active);
                }
                this.removeTagFromFilters(tag);
            }
        });
        this.activeTags = this.filters;
        let newHash = this.getTagsLocationHash();

        if (newHash && newHash.length > 1) {
            location.hash = newHash;
        }
    }

    private getTagsFromLocationHash(): Array<string> {
        return location.hash
            .split('/')
            .filter(o => ['', '#'].indexOf(o) === -1);
    }

    private getTagsLocationHash(): string {
        return '/' + this.activeTags.join('/');
    }

    private toggleTag(tag: Element): void {
        const doActivateTag = !this.isTagActive(tag);
        const tagsDropdown = getParentWithClass(
            <HTMLElement>tag,
            this._options.selectors.tagsDropdown
        );
        if (tagsDropdown != null) {
            this.deactivateActiveTags(tagsDropdown);
            if (doActivateTag) {
                this.activateTag(tag);
            } else {
                this.deactivateTag(tag);
            }
        } else {
            if (doActivateTag) {
                if (hasClass(tag, this._options.selectors.tagCategory) && this.tagsCategory != null) {
                    this.tags.filter(o => this.isTagActive(o)).forEach(o => this.deactivateActiveTag(o));
                }
                this.activateActiveTag(tag);
            } else {
                this.deactivateActiveTag(tag);
            }
        }
    }

    private updateSelectTag(select: Element): void {
        const selectMenu = <HTMLSelectElement>select;
        const selectedIndex = selectMenu.selectedIndex;
        const selectedTag = <Element>selectMenu.options[selectedIndex];
        this.deactivateActiveTags(select);
        if (selectedTag != null) {
            this.activateActiveTag(selectedTag);
        }
    }

    private resetAllActiveTags(): void {
        this.filters = [];
        this.setFilterPageHasActiveTags();
        location.hash = '';
        this.selects.forEach(select => {
            (<HTMLSelectElement>select).selectedIndex = 0;
        });
        this.loadFilterElements();
    }

    private isSelectOption(tag: Element): boolean {
        return this.selects.indexOf(tag.parentElement) > -1;
    }

    private setFilterPageHasActiveTags() {
        const hasActiveTags = this.filters != null && this.filters.length > 0;
        toggleClass(this.wrapperElement, this._options.classes.hasActiveTags, hasActiveTags);
    }

    private activateActiveTag(tag: Element) {
        addClass(tag, this._options.classes.active);
        this.activateTag(tag);
    }

    private activateTag(tag: Element) {
        const tagValue = this.getTagValue(tag);
        if (tagValue.length > 0) {
            this.activeTags.push(tagValue);
            location.hash = this.getTagsLocationHash();
        }
    }

    private deactivateTag(tag: Element) {
        const tagValue = this.getTagValue(tag);
        if (tagValue.length > 0) {
            const tagValueIndex = this.getTagValueIndex(tagValue);
            if (tagValueIndex > -1) {
                this.activeTags.splice(tagValueIndex, 1);
                location.hash = this.getTagsLocationHash();
            }
        }
    }

    private deactivateActiveTags(tagsDropdown: Element): void {
        const allTagsActive = Array.from(
            tagsDropdown.getElementsByClassName(
                this._options.selectors.tag +
                ' ' +
                this._options.selectors.tagActive
            )
        ).filter(
            tag =>
                hasClass(tag.parentElement, this._options.classes.hidden) !==
                true
        );
        allTagsActive.forEach(tag => this.deactivateTag(tag));
        const tagsActive = Array.from(
            tagsDropdown.getElementsByClassName(
                this._options.selectors.tag + ' ' + this._options.classes.active
            )
        );
        tagsActive.forEach(tag => this.deactivateActiveTag(tag));
    }

    private deactivateActiveTag(tag: Element): void {
        removeClass(tag, this._options.classes.active);
        this.deactivateTag(tag);
    }

    private allTagClick(tagsDropdown: Element) {
        this.deactivateActiveTags(tagsDropdown);
        this.activateAllTag(<HTMLElement>tagsDropdown);
    }

    private activateAllTag(tagsDropdown: HTMLElement) {
        if (this.allTags != null && this.allTags.length) {
            const allTag = tagsDropdown.getElementsByClassName(
                this._options.selectors.tagAll
            )[0];
            const allTagActive = tagsDropdown.getElementsByClassName(
                this._options.selectors.tagAllActive
            )[0];
            const dropdownToggleBtn = getParentWithClass(
                tagsDropdown,
                this._options.selectors.dropdown
            ).getElementsByClassName(this._options.selectors.dropdownToggle)[0];
            const filterAll = this.getTagValue(allTag);

            if (filterAll !== 'undefined' && filterAll !== '') {
                this.addTagToFilters(allTag);
            }
            addClass(allTag.parentElement, this._options.classes.hidden);
            removeClass(
                allTagActive.parentElement,
                this._options.classes.hidden
            );
            removeClass(dropdownToggleBtn, this._options.classes.active);
        }
    }

    private deactivateAllTag(tagsDropdown: HTMLElement) {
        if (this.allTags != null && this.allTags.length) {
            const allTag = tagsDropdown.getElementsByClassName(
                this._options.selectors.tagAll
            )[0];
            const allTagActive = tagsDropdown.getElementsByClassName(
                this._options.selectors.tagAllActive
            )[0];
            const dropdownToggleBtn = getParentWithClass(
                tagsDropdown,
                this._options.selectors.dropdown
            ).getElementsByClassName(this._options.selectors.dropdownToggle)[0];
            const filterAll = this.getTagValue(allTag);

            if (filterAll !== 'undefined' && filterAll !== '') {
                this.removeTagFromFilters(allTag);
            }
            removeClass(allTag.parentElement, this._options.classes.hidden);
            addClass(allTagActive.parentElement, this._options.classes.hidden);
            addClass(dropdownToggleBtn, this._options.classes.active);
        }
    }

    private isTagActive(tag: Element): boolean {
        return this.getTagValueIndex(this.getTagValue(tag)) > -1;
    }

    private getTagValueIndex(tagValue: string): number {
        return this.activeTags.indexOf(tagValue);
    }

    private getTagValue(tag: Element): string {
        return tag.getAttribute(this._options.tagValue);
    }

    private getFilters(): string {
        return this.filters.toString();
    }

    private addTagToFilters(tag: Element) {
        const tagValue = this.getTagValue(tag);

        if (tagValue !== 'undefined' && tagValue !== '') {
            if (this.filters.indexOf(tagValue) < 0) {
                this.filters.push(tagValue);
            }
        }
    }
    private removeTagFromFilters(tag: Element) {
        const tagValue = this.getTagValue(tag);

        if (tagValue !== 'undefined' && tagValue !== '') {
            const indexFilter = this.filters.indexOf(tagValue);

            if (indexFilter > -1) {
                this.filters.splice(indexFilter, 1);
            }
        }
    }
}

export default function filterpage(
    wrapper: HTMLElement,
    options?: FilterPageOptions
): FilterPage {
    return new FilterPage(wrapper, options);
}
